import { signInWithPopup } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';

import { auth } from '../../index.js';
import { db } from '../../index.js';
import { googleProvider } from '../../index.js';

/**
 * Check if a user document has all required fields
 * @param {Object} userData - The user document data
 * @returns {boolean} - True if all required fields exist
 */
const hasCompleteUserData = (userData) => {
  const requiredFields = [
    'uid',
    'email',
    'username',
    'highScore',
    'highScoreToday',
    'membershipStatus',
    'trialCredits',
  ];

  return requiredFields.every((field) => userData.hasOwnProperty(field));
};

/**
 * Handle Google Sign Up - Creates a new user account or completes incomplete accounts
 * @param {Function} navigate - React Router navigate function
 * @param {Function} setIsLoading - State setter for loading indicator
 */
export const signUpWithGoogle = async (navigate, setIsLoading) => {
  try {
    setIsLoading(true);

    sessionStorage.setItem('isSigningUp', 'true');

    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    if (!user || !user.uid) {
      sessionStorage.removeItem('isSigningUp');
      toast.error('Invalid user data received from Google.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      setIsLoading(false);
      return;
    }

    // Force token refresh to ensure authentication is complete
    await user.getIdToken(true);

    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);

    // Check if user exists AND has complete data
    if (userDoc.exists()) {
      const userData = userDoc.data();

      // If they have complete data, they should use login
      if (hasCompleteUserData(userData)) {
        sessionStorage.removeItem('isSigningUp');
        toast.error('Account already exists. Please use the login page.', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
        });
        await auth.signOut();
        setIsLoading(false);
        return;
      }
    }

    // Create/complete user account
    const sessionToken = uuidv4();
    localStorage.setItem('sessionToken', sessionToken);

    const lowerCaseUsername = user.email
      .split('@')[0]
      .replace(/[^a-zA-Z0-9]/g, '_')
      .toLowerCase();

    // Write to Firestore
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email,
      username: lowerCaseUsername,
      highScore: 0,
      highScoreToday: 0,
      membershipStatus: 'Basic',
      trialCredits: 50,
      sessionToken,
    });

    // Wait a moment for Firestore to sync
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Verify the document was actually created
    const verifyDoc = await getDoc(userDocRef);

    if (!verifyDoc.exists()) {
      console.error(
        '❌ Document was not created! Auth state:',
        auth.currentUser?.uid,
      );
      sessionStorage.removeItem('isSigningUp');
      toast.error('Account creation failed. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      setIsLoading(false);
      return;
    }

    // Verify user is still authenticated
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error('❌ User is not authenticated after signup!');
      sessionStorage.removeItem('isSigningUp');
      toast.error('Authentication lost. Please try logging in.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      setIsLoading(false);
      return;
    }

    const message = userDoc.exists()
      ? 'Account completed successfully! Welcome!'
      : 'Account created successfully! Welcome!';

    // Clear the flag BEFORE navigating
    sessionStorage.removeItem('isSigningUp');

    navigate('/flashcards');
  } catch (err) {
    console.error('❌ ERROR:', err);
    sessionStorage.removeItem('isSigningUp');

    if (
      err.code === 'auth/popup-closed-by-user' ||
      err.code === 'auth/cancelled-popup-request'
    ) {
      setIsLoading(false);
      return;
    }

    toast.error('Failed to sign up with Google. Please try again.', {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
    });

    setIsLoading(false);
  }
};

/**
 * Handle Google Login - Logs in an existing user
 * @param {Function} navigate - React Router navigate function
 * @param {Function} setIsLoading - State setter for loading indicator
 */
export const loginWithGoogle = async (navigate, setIsLoading) => {
  try {
    setIsLoading(true);

    // Set a flag to prevent route guards from interfering during login
    sessionStorage.setItem('isLoggingIn', 'true');

    await signInWithPopup(auth, googleProvider);

    const user = auth.currentUser;

    if (!user) {
      sessionStorage.removeItem('isLoggingIn');
      throw new Error('Auth user is null after popup');
    }

    await user.getIdToken(true);

    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      sessionStorage.removeItem('isLoggingIn');
      toast.error('Account not found. Please sign up first.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      await auth.signOut();
      setIsLoading(false);
      return;
    }

    // Check if user has complete data
    const userData = userDoc.data();
    if (!hasCompleteUserData(userData)) {
      sessionStorage.removeItem('isLoggingIn');
      toast.error(
        'Account is incomplete. Please use the sign up page to complete your account.',
        {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
        },
      );
      await auth.signOut();
      setIsLoading(false);
      return;
    }

    const sessionToken = uuidv4();
    localStorage.setItem('sessionToken', sessionToken);

    await updateDoc(userDocRef, {
      sessionToken: sessionToken,
    });

    // Clear the flag BEFORE navigating
    sessionStorage.removeItem('isLoggingIn');

    navigate('/flashcards');
  } catch (err) {
    console.error('Google sign-in failed:', err);
    sessionStorage.removeItem('isLoggingIn');

    if (
      err.code === 'auth/popup-closed-by-user' ||
      err.code === 'auth/cancelled-popup-request'
    ) {
      setIsLoading(false);
      return;
    }

    toast.error('Failed to sign in with Google.', {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
    });

    setIsLoading(false);
  }
};
